import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Check, Clock, Trash2, Users } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMING_LABELS = {
  august_2026: "Aug/Fall 2026",
  spring_2027: "Spring/Summer 2027",
  anytime: "Anytime",
  // legacy values from older entries:
  august_2026_test: "Aug/Fall 2026",
  "2027_opening": "Spring/Summer 2027",
};

const SITE_LABELS = {
  site_one: "Site One",
  site_two: "Site Two",
  either: "Either",
};

const statusColors = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-green-100 text-green-700",
  archived: "bg-gray-100 text-gray-500",
};

export default function WaitlistManager({ authHeader }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/waitlist`, authHeader);
      setItems(res.data.waitlist);
    } catch (e) {
      console.error(e);
    }
  }, [authHeader]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/admin/waitlist/${id}/status`, { status }, authHeader);
    fetchItems();
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Remove this entry from the waitlist?")) return;
    await axios.delete(`${API}/admin/waitlist/${id}`, authHeader);
    fetchItems();
  };

  const byStatus = filter === "all" ? items : items.filter(i => (i.status || "new") === filter);
  const filtered = siteFilter === "all" ? byStatus : byStatus.filter(i => (i.interested_in || "either") === siteFilter);
  const counts = {
    all: items.length,
    new: items.filter(i => (i.status || "new") === "new").length,
    contacted: items.filter(i => i.status === "contacted").length,
    archived: items.filter(i => i.status === "archived").length,
  };
  const siteCounts = {
    all: items.length,
    site_one: items.filter(i => i.interested_in === "site_one").length,
    site_two: items.filter(i => i.interested_in === "site_two").length,
    either: items.filter(i => (i.interested_in || "either") === "either").length,
  };

  return (
    <div data-testid="waitlist-manager">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="font-display text-wc-forest text-lg font-semibold flex items-center gap-2">
            <Users className="w-5 h-5" /> Waitlist ({items.length})
          </h3>
          <div className="flex gap-1 text-xs">
            {["all", "new", "contacted", "archived"].map(s => (
              <button key={s} onClick={() => setFilter(s)}
                data-testid={`waitlist-filter-${s}`}
                className={`px-3 py-1.5 rounded font-body capitalize transition-colors ${
                  filter === s ? "bg-wc-teal text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}>
                {s} ({counts[s]})
              </button>
            ))}
          </div>
        </div>

        {/* Site filter */}
        <div className="flex items-center gap-2 mb-4 flex-wrap text-xs">
          <span className="font-body text-gray-500 uppercase tracking-wider">Site:</span>
          {[
            { key: "all", label: "All" },
            { key: "site_one", label: "Site One" },
            { key: "site_two", label: "Site Two" },
            { key: "either", label: "Either" },
          ].map(s => (
            <button key={s.key} onClick={() => setSiteFilter(s.key)}
              data-testid={`waitlist-site-filter-${s.key}`}
              className={`px-3 py-1.5 rounded font-body transition-colors ${
                siteFilter === s.key ? "bg-wc-forest text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}>
              {s.label} ({siteCounts[s.key]})
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <p className="font-body text-gray-400 text-sm py-8 text-center">No entries in this view yet.</p>
        ) : (
          <div className="space-y-3">
            {filtered.map(item => {
              const status = item.status || "new";
              return (
                <div key={item.id} className={`border rounded-md p-4 ${status === "new" ? "border-wc-teal/30 bg-wc-teal/5" : "border-gray-100 bg-white"}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-display text-wc-forest font-semibold text-sm">{item.name}</span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-body uppercase tracking-wide ${statusColors[status] || statusColors.new}`}>
                          {status}
                        </span>
                        {item.interested_in && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-body bg-wc-teal/10 text-wc-teal font-semibold">
                            {SITE_LABELS[item.interested_in] || item.interested_in}
                          </span>
                        )}
                        {item.preferred_timing && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-body bg-wc-forest/10 text-wc-forest">
                            {TIMING_LABELS[item.preferred_timing] || item.preferred_timing}
                          </span>
                        )}
                      </div>
                      <p className="font-body text-gray-600 text-xs mb-2">
                        <a href={`mailto:${item.email}`} className="hover:text-wc-teal">{item.email}</a>
                        {item.phone && <> · {item.phone}</>}
                        {item.group_size && <> · {item.group_size} guests</>}
                      </p>
                      {item.message && <p className="font-body text-gray-700 text-sm mb-2 italic">"{item.message}"</p>}
                      <p className="font-body text-gray-300 text-xs">{new Date(item.created_at).toLocaleString()}</p>
                    </div>
                    <div className="flex gap-1">
                      {status !== "contacted" && (
                        <Button size="sm" variant="ghost" onClick={() => updateStatus(item.id, "contacted")}
                          data-testid={`waitlist-mark-contacted-${item.id}`}
                          className="text-green-600 hover:bg-green-50 h-8 w-8 p-0" title="Mark as contacted">
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                      {status !== "new" && (
                        <Button size="sm" variant="ghost" onClick={() => updateStatus(item.id, "new")}
                          className="text-blue-600 hover:bg-blue-50 h-8 w-8 p-0" title="Mark as new">
                          <Clock className="w-4 h-4" />
                        </Button>
                      )}
                      {status !== "archived" && (
                        <Button size="sm" variant="ghost" onClick={() => updateStatus(item.id, "archived")}
                          className="text-gray-500 hover:bg-gray-100 h-8 w-8 p-0" title="Archive">
                          <Clock className="w-4 h-4 opacity-40" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => deleteEntry(item.id)}
                        data-testid={`waitlist-delete-${item.id}`}
                        className="text-red-500 hover:bg-red-50 h-8 w-8 p-0" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
